import { lazy } from "react";
import Icon from "@mui/material/Icon";

// ─── Lazy-loaded layouts ──────────────────────────────────────────────────────
const Dashboard = lazy(() => import("layouts/dashboard-router"));
const Plog = lazy(() => import("layouts/plog"));
const PlogCreate = lazy(() => import("layouts/plog/create"));
const PlogUpdate = lazy(() => import("layouts/plog/update"));
const Profile = lazy(() => import("layouts/profile"));
const SignIn = lazy(() => import("layouts/authentication/sign-in"));

const ManageUser = lazy(() => import("layouts/manage-user/user"));
const CreateUser = lazy(() => import("layouts/manage-user/user/create"));
const UpdateUser = lazy(() => import("layouts/manage-user/user/update"));
const ManagePosition = lazy(() => import("layouts/manage-user/position"));
const UserAssignments = lazy(() => import("layouts/manage-user/user/assignments"));
const AssignMenu = lazy(() => import("layouts/manage-user/position/assign-menu"));
const UserDPlPermission = lazy(() => import("layouts/manage-user/user/assign-dpl-permission"));
const ResetPassword = lazy(() => import("layouts/manage-user/user/reset-password"));
const CreatePosition = lazy(() => import("layouts/manage-user/position/create"));
const ManageMenu = lazy(() => import("layouts/manage-user/menu"));
const CreateMenu = lazy(() => import("layouts/manage-user/menu/create"));

const HelpDesk = lazy(() => import("layouts/helpdesk"));
const HelpdeskDetail = lazy(() => import("layouts/helpdesk/update"));

const PurchaseOrderKf = lazy(() => import("layouts/purchase_order_kf"));
const PurchaseOrderDetail = lazy(() => import("layouts/purchase_order_kf/detail"));
const PurchaseOrderDetailReview = lazy(() => import("layouts/purchase_order_kf/review-print"));
const ReportPlog = lazy(() => import("layouts/plog/report-plog"));

const UserPrincipals = lazy(() => import("layouts/manage-principals/user"));
const UpdateUserPrincipals = lazy(() => import("layouts/manage-principals/user/update"));
const PrincipalsRole = lazy(() => import("layouts/manage-principals/principal-role"));
const PrincipalRoleUpdate = lazy(() => import("layouts/manage-principals/principal-role/update"));
const UserExternalDPlPermission = lazy(() =>
  import("layouts/manage-principals/user/dpl-permission")
);
const UserOutletPermission = lazy(() => import("layouts/manage-principals/user/outlet-permission"));
const UserSetCoverage = lazy(() => import("layouts/manage-principals/user/set-coverage"));
const UserPrincipalCreate = lazy(() => import("layouts/manage-principals/user/create"));
const ResetPasswordPrincipal = lazy(() => import("layouts/manage-principals/user/reset-password"));
const PositionItemPermission = lazy(() =>
  import("layouts/manage-principals/principal-role/plafon-item-permmision")
);
const ItemDiscPo = lazy(() => import("layouts/manage-principals/principal-role/item-disc-po"));
const ItemDiscPoClaimReport = lazy(() =>
  import("layouts/manage-principals/principal-role/item-disc-po/ClaimReport")
);
const PositionItemPermissionForPrincipal = lazy(() =>
  import("layouts/manage-principals/plafon-item-permmision")
);

const DplList = lazy(() => import("layouts/manage-dpl"));
const DplCreate = lazy(() => import("layouts/manage-dpl/create"));
const DplUpdate = lazy(() => import("layouts/manage-dpl/update"));
const DplCLose = lazy(() => import("layouts/manage-dpl/dpl-close"));

const PacketList = lazy(() => import("layouts/manage-packet"));
const PacketCreate = lazy(() => import("layouts/manage-packet/create"));
const PacketUpdate = lazy(() => import("layouts/manage-packet/update"));

const LoaList = lazy(() => import("layouts/manage-loa"));
const LoaSection = lazy(() => import("layouts/manage-loa/loa-section"));
const LoaSectionDetail = lazy(() => import("layouts/manage-loa/loa-section-detail"));
const LoaSectionDetailItem = lazy(() => import("layouts/manage-loa/loa-section-detail-item"));
const LoaRole = lazy(() => import("layouts/manage-loa/loa-role"));
const UpdateLoaRole = lazy(() => import("layouts/manage-loa/loa-role/update"));
const LoaStatus = lazy(() => import("layouts/manage-loa/loa-status"));
const LoaSectionDetailCreate = lazy(() => import("layouts/manage-loa/loa-section-detail/create"));
const LoaSectionDetailItemCreate = lazy(() =>
  import("layouts/manage-loa/loa-section-detail-item/create")
);

const MasterPrincipal = lazy(() => import("layouts/master-data/principal"));
const MasterPrincipalRoleCreate = lazy(() => import("layouts/master-data/principal/create"));
const MasterPrincipalAssignDplType = lazy(() =>
  import("layouts/master-data/principal/set-dpl-type")
);
const MasterPrincipalUpdate = lazy(() => import("layouts/master-data/principal/update"));
const MasterPrincipalPosition = lazy(() => import("layouts/master-data/principal/position"));
const MasterPrincipalApprovalStatus = lazy(() =>
  import("layouts/master-data/principal/approval_status")
);
const MasterPrincipalAssignSegment = lazy(() =>
  import("layouts/master-data/principal/set-segment")
);
const MasterPrincipalAssignClassItem = lazy(() =>
  import("layouts/master-data/principal/set-class-item")
);
const MasterPrincipalAssignCategoryProduct = lazy(() =>
  import("layouts/master-data/principal/set-product-category")
);
const MasterPrincipalAssignColumn = lazy(() =>
  import("layouts/master-data/principal/set-discount-column")
);

const SupplyRequest = lazy(() => import("layouts/supply-request"));
const SupplyRequestCreate = lazy(() => import("layouts/supply-request/create"));

const TopkDashboard = lazy(() => import("layouts/etop/topk"));
const TopkFormPage = lazy(() => import("layouts/etop/topk/form"));
const EtopApproval = lazy(() => import("layouts/etop/approval"));
const B2bDashboard = lazy(() => import("layouts/etop/b2b"));
const B2bFormPage = lazy(() => import("layouts/etop/b2b/form"));
const EtopReport = lazy(() => import("layouts/etop/report"));

const SalesOrder = lazy(() => import("layouts/manage-transactions/sales-order"));
const CustomerApproval = lazy(() => import("layouts/customer-approval"));

const MappingOutletKf = lazy(() => import("layouts/mapping-kf/outlet"));
const MappingProductKf = lazy(() => import("layouts/mapping-kf/product"));
const EditProduct = lazy(() => import("layouts/mapping-kf/product/edit"));
const EditOutlet = lazy(() => import("layouts/mapping-kf/outlet/edit"));
const DoubleDetail = lazy(() => import("layouts/mapping-kf/outlet/double/detail"));
const DoubleProduct = lazy(() => import("layouts/mapping-kf/product/double/detail"));

const MappingOutletPrincipal = lazy(() => import("layouts/mapping-principal/outlet"));
const MappingItemPrincipal = lazy(() => import("layouts/mapping-principal/item"));
const PriceSetting = lazy(() => import("layouts/price-management/price-setting"));

const InventoryAssets = lazy(() => import("layouts/inventory/assets"));
const MasterAsset = lazy(() => import("layouts/inventory/master-asset"));
const UpdateItem = lazy(() => import("layouts/inventory/item/update"));
const CreatePacketItem = lazy(() => import("layouts/inventory/packet_item/create"));
const UpdatePacketItem = lazy(() => import("layouts/inventory/packet_item/update"));
const GoodsReceipt = lazy(() => import("layouts/inventory/goods_receipt"));
const UpdatePurchaseOrders = lazy(() => import("layouts/inventory/purchase_order/update"));
const UpdateGoodsReceipt = lazy(() => import("layouts/inventory/goods_receipt/update"));
const CreateGoodsReceipt = lazy(() => import("layouts/inventory/goods_receipt/create"));
const PurchaseManagement = lazy(() => import("layouts/inventory/purchase_order"));
const InventoryRequestPage = lazy(() =>
  import("layouts/inventory/inv_request/InventoryRequestPage")
);
const InventoryRequest = lazy(() => import("layouts/inventory/inv_request"));
const Procurement = lazy(() => import("layouts/inventory/procurement"));
const InventoryProcurementPending = lazy(() => import("layouts/inventory/procurement/capex"));
const ProcurementDetail = lazy(() =>
  import("layouts/inventory/procurement/InventoryRequest/ProcurementDetail")
);
const ApprovalDetail = lazy(() =>
  import("layouts/inventory/procurement/InventoryRequest/ApprovalDetail")
);
const SalesOrderApproval = lazy(() => import("layouts/sales-order-approval"));
const SalesOrderApprovalDetail = lazy(() => import("layouts/sales-order-approval/detail"));
const SalesOrderApprovalHistory = lazy(() => import("layouts/sales-order-approval/history"));
const SalesOrderApprovalHistoryDetail = lazy(() =>
  import("layouts/sales-order-approval/history-detail")
);
// ─── Routes ───────────────────────────────────────────────────────────────────
// component = lazy component class (not JSX)
// protected: false = no auth guard (default: true)

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    noCollapse: true,
    component: Dashboard,
  },
  {
    type: "item",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: SignIn,
    protected: false,
  },
  {
    type: "collapse",
    name: "User Management",
    key: "manage-user",
    icon: <Icon fontSize="small">person</Icon>,
    roles: [1, 4],
    collapse: [
      {
        name: "User MPI",
        route: "/user",
        key: "manage-user",
        icon: <Icon fontSize="small">person</Icon>,
        component: ManageUser,
      },
      {
        name: "Position",
        route: "/user/position",
        key: "manage-position",
        icon: <Icon fontSize="small">person</Icon>,
        component: ManagePosition,
      },
      {
        name: "Menu",
        route: "/user/menu",
        key: "manage-menu",
        icon: <Icon fontSize="small">person</Icon>,
        component: ManageMenu,
      },
    ],
  },
  {
    type: "collapse",
    name: "P-LOG",
    key: "p-log",
    icon: <Icon fontSize="small">table_view</Icon>,
    collapse: [
      {
        name: "P-LOG",
        route: "/plog",
        key: "p-log",
        icon: <Icon fontSize="small">receipt_long</Icon>,
        component: Plog,
      },
      {
        name: "Report Plog",
        route: "/report-plog",
        key: "report-plog",
        icon: <Icon fontSize="small">Report Plog</Icon>,
        component: ReportPlog,
      },
    ],
  },
  {
    type: "item",
    name: "Create Plog",
    key: "create-plog",
    route: "/plog/create",
    component: PlogCreate,
  },
  {
    type: "item",
    name: "Update Plog",
    key: "update-plog",
    route: "/plog/update/:id",
    component: PlogUpdate,
  },
  {
    type: "collapse",
    name: "HelpDesk",
    key: "helpdesk",
    icon: <Icon fontSize="small">help</Icon>,
    collapse: [
      {
        name: "HelpDesk",
        route: "/helpdesk",
        key: "helpdesk",
        icon: <Icon fontSize="small">help</Icon>,
        component: HelpDesk,
      },
    ],
  },
  {
    type: "collapse",
    name: "HelpDesk Mis",
    key: "helpdesk-mis",
    icon: <Icon fontSize="small">help</Icon>,
    collapse: [
      {
        name: "HelpDesk Mis",
        route: "/helpdesk-mis",
        key: "helpdesk-mis",
        icon: <Icon fontSize="small">help</Icon>,
        component: HelpDesk,
      },
    ],
  },
  {
    type: "collapse",
    name: "Order Management KF",
    key: "order-management-kf",
    icon: <Icon fontSize="small">Report</Icon>,
    collapse: [
      {
        name: "Purchase Order",
        route: "/purchase-order",
        key: "purchase-order",
        icon: <Icon fontSize="small">Report</Icon>,
        component: PurchaseOrderKf,
      },
      {
        name: "Mapping Outlet",
        route: "/mapping-outlet",
        key: "mapping",
        icon: <Icon fontSize="small">table_view</Icon>,
        component: MappingOutletKf,
      },
      {
        name: "Mapping Product",
        route: "/mapping-product",
        key: "mapping-product",
        icon: <Icon fontSize="small">table_view</Icon>,
        component: MappingProductKf,
      },
    ],
  },
  {
    type: "item",
    name: "Mapping Outlet",
    key: "mapping",
    route: "/mapping-outlet/detail/:id",
    component: DoubleDetail,
  },
  {
    type: "item",
    name: "Edit Product",
    key: "mapping",
    route: "/mapping-outlet/edit/:id",
    component: EditOutlet,
  },
  {
    type: "item",
    name: "Mapping Product",
    key: "mapping",
    route: "/mapping-product/detail/:id",
    component: DoubleProduct,
  },
  {
    type: "item",
    name: "Edit Product",
    key: "mapping",
    route: "/mapping-product/edit/:id",
    component: EditProduct,
  },
  {
    type: "collapse",
    name: "Principal Management",
    key: "user-principals",
    icon: <Icon fontSize="small">person</Icon>,
    collapse: [
      {
        name: "Master Disc Principal",
        route: "/master-principal",
        key: "master-principal",
        icon: <Icon fontSize="small">money</Icon>,
        component: MasterPrincipal,
      },
      {
        name: "User Principal",
        route: "/user-principals",
        key: "user-principals",
        icon: <Icon fontSize="small">person</Icon>,
        component: UserPrincipals,
      },

      {
        name: "Role Principal",
        route: "/role-principal",
        key: "principal-role",
        icon: <Icon fontSize="small">person</Icon>,
        component: PrincipalsRole,
      },
      {
        name: "Mapping Outlet",
        route: "/mapping-principal-outlet",
        key: "mapping-principal-outlet",
        icon: <Icon fontSize="small">table_view</Icon>,
        component: MappingOutletPrincipal,
      },
      {
        name: "Mapping Item",
        route: "/mapping-principal-item",
        key: "mapping-principal-item",
        icon: <Icon fontSize="small">table_view</Icon>,
        component: MappingItemPrincipal,
      },
      {
        name: "Setup Plafon",
        route: "/dpl-plafon",
        key: "dpl-plafon",
        icon: <Icon fontSize="small">person</Icon>,
        component: PositionItemPermissionForPrincipal,
      },
      {
        name: "Setup Disc PO",
        route: "/item-disc-po",
        key: "item-disc-po",
        icon: <Icon fontSize="small">percent</Icon>,
        component: ItemDiscPo,
      },
      {
        name: "Report Claim Disc PO",
        route: "/item-disc-po/claim-report",
        key: "item-disc-po-claim-report",
        icon: <Icon fontSize="small">receipt_long</Icon>,
        component: ItemDiscPoClaimReport,
      },
      {
        name: "Packet List",
        route: "/packet",
        key: "packet-list",
        icon: <Icon fontSize="small">person</Icon>,
        component: PacketList,
      },
    ],
  },
  {
    type: "collapse",
    name: "Discount Management",
    key: "dpl-management",
    icon: <Icon fontSize="small">person</Icon>,
    collapse: [
      {
        name: "Discount List",
        route: "/dpl-list",
        key: "dpl-list",
        icon: <Icon fontSize="small">person</Icon>,
        component: DplList,
      },
      {
        name: "Create Discount",
        route: "/dpl-create",
        key: "dpl-create",
        icon: <Icon fontSize="small">person</Icon>,
        component: DplCreate,
      },
      {
        name: "Close Discount",
        route: "/dpl-close",
        key: "dpl-close",
        icon: <Icon fontSize="small">person</Icon>,
        component: DplCLose,
      },
    ],
  },
  {
    type: "item",
    name: "Packet Create",
    route: "/packet/create",
    key: "create-packet",
    component: PacketCreate,
  },
  {
    type: "item",
    name: "Update Packet",
    key: "update-packet",
    route: "/packet/:id",
    component: PacketUpdate,
  },
  {
    type: "collapse",
    name: "Loa Management",
    key: "loa-management",
    icon: <Icon fontSize="small">book</Icon>,
    collapse: [
      {
        name: "Loa Transaction",
        route: "/loa-list",
        key: "loa-list",
        icon: <Icon fontSize="small">money</Icon>,
        component: LoaList,
      },
      {
        name: "Loa Position Role",
        route: "/loa-position-role",
        key: "loa-role",
        icon: <Icon fontSize="small">widget</Icon>,
        component: LoaRole,
      },
      {
        name: "Loa Section",
        route: "/loa-section",
        key: "loa-section",
        icon: <Icon fontSize="small">widget</Icon>,
        component: LoaSection,
      },
      {
        name: "Loa Status",
        route: "/loa-status",
        key: "loa-status",
        icon: <Icon fontSize="small">widget</Icon>,
        component: LoaStatus,
      },
    ],
  },
  {
    type: "collapse",
    name: "Data Sales",
    key: "transaction-sales",
    icon: <Icon fontSize="small">traffic</Icon>,
    collapse: [
      {
        name: "Sales Order",
        route: "/sales-order",
        key: "sales-order",
        icon: <Icon fontSize="small">widget</Icon>,
        component: SalesOrder,
      },
    ],
  },
  {
    type: "collapse",
    name: "Apj/Pjt Platform",
    key: "customer-approval",
    icon: <Icon fontSize="small">checklist</Icon>,
    collapse: [
      {
        name: "Customer Data Platform",
        route: "/customer-data-platform",
        key: "customer-approval",
        icon: <Icon fontSize="small">checklist</Icon>,
        component: CustomerApproval,
      },
      {
        name: "Pharmacist Approval System",
        route: "/pharmacist-approval-system",
        key: "sales-order-approval",
        icon: <Icon fontSize="small">checklist</Icon>,
        component: SalesOrderApproval,
      },
      {
        name: "History Approval",
        route: "/pharmacist-approval/history",
        key: "sales-order-approval-history",
        icon: <Icon fontSize="small">history</Icon>,
        component: SalesOrderApprovalHistory,
      },
    ],
  },
  {
    type: "item",
    name: "Sales Order Approval Detail",
    key: "sales-order-approval-detail",
    route: "/pharmacist-approval-system/detail/:id",
    component: SalesOrderApprovalDetail,
  },
  {
    type: "item",
    name: "History Approval Detail",
    key: "sales-order-approval-history-detail",
    route: "/pharmacist-approval-system/history/:id",
    component: SalesOrderApprovalHistoryDetail,
  },
  {
    type: "collapse",
    name: "Asset Management",
    key: "inventory-management",
    icon: <Icon fontSize="small">money</Icon>,
    collapse: [
      {
        name: "Request Management",
        route: "/inventory-request",
        key: "inv-request",
        icon: <Icon fontSize="small">request_page</Icon>,
        component: InventoryRequest,
      },
      {
        name: "Create Capex Request",
        route: "/inventory-procurement-pending",
        key: "procurement-pending",
        icon: <Icon fontSize="small">shopping_cart</Icon>,
        component: InventoryProcurementPending,
      },
      {
        name: "Capex Approval",
        route: "/inventory-procurement",
        key: "procurement",
        icon: <Icon fontSize="small">shopping_cart</Icon>,
        component: Procurement,
      },
      {
        name: "Purchase Management",
        route: "/purchase-orders",
        key: "inv-purchase-order",
        icon: <Icon fontSize="small">request_page</Icon>,
        component: PurchaseManagement,
      },
      {
        name: "Goods Receipt",
        route: "/inventory-goods-receipt",
        key: "inv-delivery-order",
        icon: <Icon fontSize="small">request_page</Icon>,
        component: GoodsReceipt,
      },
      {
        name: "Assets List",
        route: "/inventory-assets",
        key: "inv-asset",
        icon: <Icon fontSize="small">business_center</Icon>,
        component: InventoryAssets,
      },
      {
        name: "Master Asset",
        route: "/inventory-item",
        key: "inv-item",
        icon: <Icon fontSize="small">list</Icon>,
        component: MasterAsset,
      },
    ],
  },
  {
    type: "collapse",
    name: "Supply Request",
    key: "supply-request",
    icon: <Icon fontSize="small">local_shipping</Icon>,
    collapse: [
      {
        name: "Supply Request (ASR)",
        route: "/supply-request",
        key: "supply-request",
        icon: <Icon fontSize="small">local_shipping</Icon>,
        component: SupplyRequest,
      },
    ],
  },
  {
    type: "collapse",
    name: "e-TOPK & B2B",
    key: "etop",
    icon: <Icon fontSize="small">payments</Icon>,
    collapse: [
      {
        name: "TOPK Dashboard",
        route: "/topk-dashboard",
        key: "topk-dashboard",
        icon: <Icon fontSize="small">dashboard</Icon>,
        component: TopkDashboard,
      },
      {
        name: "B2B Dashboard",
        route: "/b2b-dashboard",
        key: "b2b-dashboard",
        icon: <Icon fontSize="small">dashboard</Icon>,
        component: B2bDashboard,
      },
      {
        name: "Approval TOPK & B2B",
        route: "/etop-approval",
        key: "etop-approval",
        icon: <Icon fontSize="small">pending_actions</Icon>,
        component: EtopApproval,
      },
      {
        name: "Report TOPK & B2B",
        route: "/etop-report",
        key: "etop-report",
        icon: <Icon fontSize="small">assessment</Icon>,
        component: EtopReport,
      },
    ],
  },
  {
    type: "item",
    name: "TOPK Dashboard",
    key: "topk-dashboard",
    route: "/topk",
    component: TopkDashboard,
  },
  {
    type: "item",
    name: "B2B Dashboard",
    key: "b2b-dashboard",
    route: "/b2b",
    component: B2bDashboard,
  },
  {
    type: "item",
    name: "TOPK Dashboard",
    key: "topk-dashboard",
    route: "/etop-dashboard",
    component: TopkDashboard,
  },
  {
    type: "item",
    name: "Create TOPK",
    key: "topk-create",
    route: "/topk/create",
    component: TopkFormPage,
  },
  {
    type: "item",
    name: "Edit TOPK",
    key: "topk-edit",
    route: "/topk/edit/:id",
    component: TopkFormPage,
  },
  {
    type: "item",
    name: "Revise TOPK",
    key: "topk-revise",
    route: "/topk/revise/:id",
    component: TopkFormPage,
  },
  {
    type: "item",
    name: "Create B2B",
    key: "b2b-create",
    route: "/b2b/create",
    component: B2bFormPage,
  },
  {
    type: "item",
    name: "Edit B2B",
    key: "b2b-edit",
    route: "/b2b/edit/:id",
    component: B2bFormPage,
  },
  {
    type: "item",
    name: "Revise B2B",
    key: "b2b-revise",
    route: "/b2b/revise/:id",
    component: B2bFormPage,
  },
  {
    type: "item",
    name: "Create Supply Request",
    key: "create-supply-request",
    route: "/supply-request/create",
    component: SupplyRequestCreate,
  },
  {
    type: "item",
    name: "Update Supply Request",
    key: "update-supply-request",
    route: "/supply-request/update/:id",
    component: SupplyRequestCreate,
  },
  {
    type: "item",
    name: "Create Request",
    key: "create-request",
    route: "/inventory-request/create",
    component: InventoryRequestPage,
  },
  {
    type: "item",
    name: "Update Request",
    key: "update-request",
    route: "/inventory-request/update/:id",
    component: InventoryRequestPage,
  },
  {
    type: "item",
    name: "Procurement Detail",
    key: "procurement-detail",
    route: "/inventory-procurement-detail/:id",
    component: ProcurementDetail,
  },
  {
    type: "item",
    name: "Approval Detail",
    key: "approval-detail",
    route: "/inventory-approval-detail/:id",
    component: ApprovalDetail,
  },
  {
    type: "item",
    name: "Update Item",
    key: "update-item",
    route: "/inventory-item/update/:id",
    component: UpdateItem,
  },
  {
    type: "item",
    name: "Create Packet Item",
    key: "create-item",
    route: "/inventory-packet/create",
    component: CreatePacketItem,
  },
  {
    type: "item",
    name: "Update Packet Item",
    key: "update-item",
    route: "/inventory-packet/update/:id",
    component: UpdatePacketItem,
  },
  {
    type: "item",
    name: "Detail receiving",
    key: "update-item",
    route: "/receiving/update/:id",
    component: UpdatePurchaseOrders,
  },
  {
    name: "Purchase Order",
    route: "/inventory-purchase-order/update/:id",
    key: "update-purchase-order",
    icon: <Icon fontSize="small">request_page</Icon>,
    component: UpdatePurchaseOrders,
  },
  {
    name: "Delivery Order",
    route: "/inventory-goods-receipt/create",
    key: "create-goods-receipt",
    icon: <Icon fontSize="small">request_page</Icon>,
    component: CreateGoodsReceipt,
  },
  {
    name: "Delivery Order",
    route: "/inventory-goods-receipt/update/:id",
    key: "update-goods-receipt",
    icon: <Icon fontSize="small">request_page</Icon>,
    component: UpdateGoodsReceipt,
  },
  {
    type: "item",
    name: "Create User",
    key: "create-user",
    route: "/user/create",
    component: CreateUser,
  },
  {
    type: "item",
    name: "Create User",
    key: "create-user",
    route: "/user/update/:id",
    component: UpdateUser,
  },
  {
    type: "item",
    name: "Manage Assignments",
    key: "assign-user",
    route: "/user/assignments/:id",
    component: UserAssignments,
  },
  {
    type: "item",
    name: "Assign Dpl",
    key: "assign-user",
    route: "/user/dpl-permission/:id",
    component: UserDPlPermission,
  },
  {
    type: "item",
    name: "Assign Menu",
    key: "assign-user",
    route: "/user/position/assign-menu/:id",
    component: AssignMenu,
  },
  {
    type: "item",
    name: "Create Position",
    key: "position",
    route: "/user/position/create",
    component: CreatePosition,
  },
  {
    type: "item",
    name: "Reset Password",
    key: "reset-password",
    route: "/user/reset-password/:id",
    component: ResetPassword,
  },
  {
    type: "item",
    name: "Create Position",
    key: "position",
    route: "/user/menu/create",
    component: CreateMenu,
  },
  {
    type: "item",
    name: "Helpdesk",
    key: "helpdesk",
    route: "/helpdesk/detail/:id",
    component: HelpdeskDetail,
  },
  {
    type: "item",
    name: "Role Principal",
    key: "user-principal",
    route: "/role-principal/:id",
    component: PrincipalRoleUpdate,
  },
  {
    type: "item",
    name: "Role Principal",
    key: "role-principal",
    route: "/role-principal/create",
    component: MasterPrincipalRoleCreate,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/role-principal/set-item-permission/:id",
    component: PositionItemPermission,
  },
  {
    type: "item",
    name: "Role Principal",
    key: "role-principal-disc-po",
    route: "/role-principal/set-disc-po/:id",
    component: ItemDiscPo,
  },
  {
    type: "item",
    name: "Role Principal",
    key: "role-principal-claim-disc-po",
    route: "/role-principal/claim-disc-po/:id",
    component: ItemDiscPoClaimReport,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principals/update/:id",
    component: UpdateUserPrincipals,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principals/dpl-permission/:id",
    component: UserExternalDPlPermission,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principals/set-outlet-permission/:id",
    component: UserOutletPermission,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principals/set-covarage/:id",
    component: UserSetCoverage,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principal/create",
    component: UserPrincipalCreate,
  },
  {
    type: "item",
    name: "User Principal",
    key: "user-principal",
    route: "/user-principal/reset-password/:id",
    component: ResetPasswordPrincipal,
  },
  {
    type: "item",
    name: "Dpl",
    key: "dpl",
    route: "/dpl/detail/:id",
    component: DplUpdate,
  },
  {
    name: "Loa Section Detail",
    route: "/loa-section/detail/:section_id",
    key: "loa-section",
    icon: <Icon fontSize="small">person</Icon>,
    component: LoaSectionDetail,
  },
  {
    name: "Loa Section Detail Item",
    route: "/loa-section/detail-item/:section_id",
    key: "loa-section",
    icon: <Icon fontSize="small">person</Icon>,
    component: LoaSectionDetailItem,
  },
  {
    name: "Loa Position Role",
    route: "/loa-role/:position_id",
    key: "loa-role",
    icon: <Icon fontSize="small">person</Icon>,
    component: UpdateLoaRole,
  },
  {
    name: "Loa Section Role",
    route: "/loa-section/detail/create/:section_id",
    key: "loa-section-detail",
    icon: <Icon fontSize="small">person</Icon>,
    component: LoaSectionDetailCreate,
  },
  {
    name: "Loa Section Role",
    route: "/loa-section/detail-item/create/:section_id",
    key: "loa-section-detail",
    icon: <Icon fontSize="small">person</Icon>,
    component: LoaSectionDetailItemCreate,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-principal/dpl-type/:id",
    component: MasterPrincipalAssignDplType,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-principal/segment/:id",
    component: MasterPrincipalAssignSegment,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-principal/class-item/:id",
    component: MasterPrincipalAssignClassItem,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-principal/category-product/:id",
    component: MasterPrincipalAssignCategoryProduct,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-principal/discount-column/:id",
    component: MasterPrincipalAssignColumn,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-princiapl/:id",
    component: MasterPrincipalUpdate,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-princiapl/position/:id",
    component: MasterPrincipalPosition,
  },
  {
    type: "item",
    name: "Master Principal",
    key: "master-principal",
    route: "/master-princiapl/approval-status/:id",
    component: MasterPrincipalApprovalStatus,
  },
  {
    type: "item",
    name: "Purchase Order",
    key: "purchase-order",
    route: "/purchase-order/detail/:id",
    component: PurchaseOrderDetail,
  },
  {
    type: "item",
    name: "Purchase Order",
    key: "purchase-order",
    route: "/purchase-order/print/:id",
    component: PurchaseOrderDetailReview,
  },
  {
    type: "collapse",
    name: "Price Management",
    key: "price-management",
    icon: <Icon fontSize="small">sell</Icon>,
    collapse: [
      {
        name: "Price Setting",
        key: "price-setting",
        route: "/price-setting",
        icon: <Icon fontSize="small">price_change</Icon>,
        component: PriceSetting,
      },
    ],
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: Profile,
    protected: false,
  },
];

export default routes;
